// ADR 28.9.26d §4 — M3's basic dialog over a native modal `<dialog>`.
//
// The frame is `extraLarge` (28dp) on `surface-container`, the title M3's
// `headlineSmall`, the body `bodyMedium` `onSurfaceVariant`, and the actions
// end-aligned. **The safe choice takes focus; order is the caller's** (ADR
// 2.10.26k §3): M3 puts dismiss first and confirm last, which every caller but
// the subscription cancel keeps, and that one puts Keep last — the trailing
// corner, whichever way `<html dir>` reads. `p-0` on the `<dialog>` and the
// padding on one inner wrapper is what lets a click on the backdrop — whose
// target is the `<dialog>` itself — be told apart from a click in the padding.
//
// Initial focus goes to the actions-row button marked `data-dialog-default`,
// else the first button there, set here rather than left to the browser's
// first-focusable rule, so it lands on the safe choice whatever the body
// contains. The marker is not only for order: a disabled first button cannot
// take `.focus()`. No portal: the element stays where it is rendered, so
// `within(row)` still finds a row's dialog.

import { useId, type ReactNode } from 'react'

import { useModalDialog } from './useModalDialog'

export interface DialogProps {
  title: ReactNode
  onClose: () => void
  /** The safe choice takes focus (`data-dialog-default`, else the first button); order is the caller's. */
  actions: ReactNode
  children?: ReactNode
}

const ACTIONS_TESTID = 'dialog-actions'

function focusFirstAction(dialog: HTMLDialogElement) {
  const row = `[data-testid="${ACTIONS_TESTID}"]`
  const target =
    dialog.querySelector<HTMLElement>(`${row} button[data-dialog-default]`) ??
    dialog.querySelector<HTMLElement>(`${row} button`)
  target?.focus()
}

export function Dialog({ title, onClose, actions, children }: DialogProps) {
  const titleId = useId()
  const ref = useModalDialog(onClose, focusFirstAction)
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className="m-auto w-[calc(100%-2rem)] max-w-md rounded-dialog bg-surface-container p-0 text-ink motion-safe:animate-reveal"
    >
      <div className="p-6">
        <h2 id={titleId} className="text-headline-sm">
          {title}
        </h2>
        {children ? <div className="mt-4 space-y-2 text-body-md text-ink-muted">{children}</div> : null}
        <div className="mt-6 flex justify-end gap-2" data-testid={ACTIONS_TESTID}>
          {actions}
        </div>
      </div>
    </dialog>
  )
}
